import {Component, OnInit, Optional, ViewChild} from '@angular/core';
import {
    AcabimCommonComponent,
    AcabimProject,
    AcabimSite,
    CapApiService,
    CapLibraryWithCapIdResponse, ColumnFilter,
    DataTableColumnData, DatatableComponent, DataTableConfig,
    DataTableObservableDataSource,
    ProjectApiService,
    SiteApiService, TableAction
} from '@cas-hub/acabim-common-client';
import {NGXLogger} from 'ngx-logger';
import {MessageService, TreeNode} from 'primeng/api';
import {of} from 'rxjs';
import {tap} from 'rxjs/operators';

export interface CapData {
    readonly id: string;
    readonly library: string;
}

export enum CapType {
    Qa = 'qa',
    Ca = 'ca',
    Root = 'root'
}

@Component({
    selector: 'acabim-admin-project-cap',
    templateUrl: './project-cap.component.html',
    styleUrls: ['./project-cap.component.scss'],
    providers: [MessageService]
})
export class ProjectCapComponent extends AcabimCommonComponent implements OnInit {

    private static DataCap = '@@CapItem[InvalidOmniClass]';

    public groupedCap: TreeNode<string>[] = [];
    public selectedCap: TreeNode<string>[] = [];
    public isLoading = false;
    public isCapTableProcessing = false;

    public datasource: DataTableObservableDataSource<CapData>[] = [];

    private currentCap: CapLibraryWithCapIdResponse[] = []; // all CAP in the system
    public currentProjectCap: CapLibraryWithCapIdResponse[] = []; // CAP attached to the selected project
    private pendingProjectUpdate?: AcabimProject; // The project pending cap update (if any, to overcome race condition)

    @ViewChild('datatable') public dataTable?: DatatableComponent;

    protected name = 'ProjectCapComponent';

    public constructor(protected readonly capApiService: CapApiService,
                       protected readonly siteApiService: SiteApiService,
                       protected readonly projectApiService: ProjectApiService,
                       @Optional() protected readonly logger?: NGXLogger) {
        super(siteApiService, projectApiService, capApiService, logger);
        this.datasource.push(new ProjectCapDataSource(() => this.currentProjectCap, this.deleteCapFromProject));
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.isLoading = true;
        this.capApiService.getAllCapLibraries().subscribe(val => {
            this.currentCap = val;
            if (this.pendingProjectUpdate) {
                this.onProjectChanged(this.pendingProjectUpdate); // update CAP info of the project
                this.pendingProjectUpdate = undefined;
            } else {
                this.refreshDisplayedCap();
            }

            this.isLoading = false;
        });
    }

    protected onProjectChanged(project?: AcabimProject | null): void {
        this.updateProjectCap(project);
    }

    protected onSiteChanged(site?: AcabimSite | null): void {
        this.selectedCap = [];
    }

    public addCapToProject(): void {
        const selectedLibraries = this.selectedCap.filter(v => v.data !== ProjectCapComponent.DataCap);
        const orphans = this.selectedCap.filter(v => {
            if (v.data !== ProjectCapComponent.DataCap) {
                return false; // ignore cap libraries
            }

            // Due to a bug in primeng, sometimes the parent is returned undefined. If the parent is returned undefined, we'll try to get the parent (cap library) from the
            // original response.
            const parent = v.parent?.label ?? this.groupedCap.find(x => x.children?.findIndex(c => c.label === v.label) !== -1)?.label;
            return selectedLibraries.findIndex(x => x.label === parent) === -1;
        });

        const selectedLibraryIds = selectedLibraries.map(x => x.data);
        const selectedCapId = orphans.filter(x => x.label).map(x => x.label);

        if (selectedLibraryIds.findIndex(x => x === null || x === undefined) !== -1) {
            // the default library was selected - add all CAP in the default lib as single cap to the project
            const children = this.groupedCap.find(x => x.label === CapApiService.DefaultLibraryName)?.children?.map(c => c.label);
            if (children && children.length) {
                selectedCapId.push(...children);
            }
        }

        if (this.currentProject) {
            this.isLoading = true;
            this.projectApiService.attachCap(this.currentProject.id, selectedCapId.filter(x => x) as string[], selectedLibraryIds.filter(x => x) as string[])
                .subscribe(_ => {
                    this.updateProjectCap(this.currentProject);
                });
        }
    }

    public deleteCapFromProject(capId: string): void {
        if (this.currentProject) {
            this.isCapTableProcessing = true;
            this.projectApiService.detachCap(this.currentProject.id, [capId])
                .pipe(tap(_ => this.isCapTableProcessing = false, _ => this.isCapTableProcessing = false))
                .subscribe(_ => this.updateProjectCap(this.currentProject));
        }
    }

    public deleteCapLibraryFromProject(capLibraryNumber: string): void {
        if (this.currentProject) {
            this.isCapTableProcessing = true;
            this.projectApiService.detachCap(this.currentProject.id, [], [capLibraryNumber])
                .pipe(tap(_ => this.isCapTableProcessing = false, _ => this.isCapTableProcessing = false))
                .subscribe(_ => this.updateProjectCap(this.currentProject));
        }
    }

    private updateProjectCap(project?: AcabimProject | null): void {
        this.selectedCap = [];
        this.currentProjectCap = [];
        if (this.currentCap.length === 0) {
            this.pendingProjectUpdate = project ?? undefined;
            return; // schedule this project to be updated next
        }

        if (project) {
            this.isLoading = true;
            this.projectApiService.getAttachedCapId(project.id).subscribe(x => {
                this.currentProjectCap = x;
                this.refreshDisplayedCap();
                this.dataTable?.requestImmediateDataRefresh();
                this.isLoading = false;
            });
        } else {
            this.currentProjectCap = [];
        }
    }

    private refreshDisplayedCap(): void {
        const convertor = (v: CapLibraryWithCapIdResponse): TreeNode<string> => {
            return {
                label: v.name,
                expandedIcon: 'pi pi-folder-open',
                collapsedIcon: 'pi pi-folder',
                children: v.capId.map(child => ({label: child, data: ProjectCapComponent.DataCap, icon: 'pi pi-file'} as TreeNode)),
                data: v.omniClassNumber, // Store the omni class as data for cap libraries
                expanded: v.name === CapApiService.DefaultLibraryName
            } as TreeNode;
        };

        // We'll not display the CAP already added to the project

        const result: TreeNode<string>[] = [];
        for (const capLib of this.currentCap) {
            const projectCapLib = this.currentProjectCap.find(x => x.name === capLib.name);
            if (projectCapLib) {
                const diffCap: string[] = capLib.capId.filter(cap => projectCapLib.capId.findIndex(x => x === cap) === -1); // all cap not added to project
                if (diffCap.length) {
                    result.push(convertor({...capLib, capId: diffCap}));
                }
            } else {
                result.push(convertor(capLib));
            }
        }

        this.groupedCap = result;
    }
}

export class ProjectCapDataSource extends DataTableObservableDataSource<CapData> {

    private static TableActionIdRemoveCap = 0;

    public readonly columnData: DataTableColumnData[] =
        [
            {displayName: 'CAP ID', objectPropertyName: 'id'},
            {displayName: 'Library', objectPropertyName: 'library'}
        ];

    public config: DataTableConfig = {
        tableHeading: '',
        isSearchEnabled: false,
        searchPlaceHolder: '',
        emptyMessage: 'No function found',
        globalFilterFields: [],
        hasCheckbox: true,
        hasDetailsButton: false,
        isLazyLoadingEnabled: false,
        isPaginationEnabled: false,
        hasFilters: false,
        isExportEnabled: false,
        hasActions: true
    };

    public columnFilters: ColumnFilter[] = [];
    public actions: TableAction[] = []; // [{name: 'REMOVE', id: ProjectCapDataSource.TableActionIdRemoveCap}];

    onDataExpanded(value: CapData): void {
    }

    constructor(private readonly dataRefresher: () => CapLibraryWithCapIdResponse[],
                private readonly deleteCapFromProject: (capId: string) => void) {
        super(() => {
            const data = this.dataRefresher()
                .map(x => x.capId.map(cap => ({id: cap, library: x.name} as CapData)))
                .reduce((result, current) => result.concat(current), []);

            return of(data);
        }, undefined, undefined);
    }

    onAction(action: TableAction): void {
        if (action.id === ProjectCapDataSource.TableActionIdRemoveCap) {
            this.deleteCapFromProject('');
        }
    }

    onDataFiltered(): void {
    }
}

//
//     deleteFromProject(): void {
//         if (this.dataSources[0].selectedData.length > 0) {
//             this.dataSources[0].selectedData.forEach((cap, index) => {
//                 // tslint:disable-next-line:no-non-null-assertion
//                 this.projectApiService.detachCap(this.selectedProject?.id!, cap.id).subscribe(data => {
//                     if (index === this.dataSources[0].selectedData.length - 1) {
//                         this.messageService.clear();
//                         this.messageService.add({
//                             severity: 'success',
//                             summary: 'Success',
//                             // @ts-ignore
//                             detail: 'Caps has been detached from ' + this.selectedProject.name
//                         });
//                     }
//                     this.dataTable?.requestImmediateDataRefresh();
//                 }, error => {
//                     this.messageService.clear();
//                     this.messageService.add({
//                         severity: 'error',
//                         summary: 'Error',
//                         // @ts-ignore
//                         detail: 'Something went wrong in detach process'
//                     });
//                 });
//
//             });
//
//         } else {
//             this.messageService.clear();
//             this.messageService.add({
//                 severity: 'error',
//                 summary: 'Error',
//                 // @ts-ignore
//                 detail: 'Please select CAP to detach'
//             });
//         }
//     }
// }
//

